import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Typo from 'constants/typography'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { withI18next } from 'lib/i18n/withI18next'
import styles from './style.scss'
import TableHeader from './table-header'
import SIZE_TABLE_DATA from './const'

/**
 * A table displays rows of data
 * @param {object[]} props.columns columns of table
 * @param {object[]} props.rows data record array which keys match with columns to be displayed
 * @param {function} t translator function
 * @param {bool} props.isPhone
 * @param {bool} props.isNewSize
 * @param {bool} props.isSizeGuideDialog
 */
const SizeTable = ({
  columns,
  rows,
  t,
  isSizeGuideDialog,
  isNewSize,
  isPhone,
}) => {
  const stickyColWidth = isSizeGuideDialog || isPhone ? 100 : 90
  const [firstCol, ...cols] = columns
  const containerRef = useRef()
  const [scrollLeft, setScrollLeft] = useState(0)
  const stickyColumnClasses = clsx('sticky-col', {
    scrolled: scrollLeft > 0,
    'inter-sizing-col': isSizeGuideDialog,
  })
  const isShowOldSizeRow = isNewSize && !isSizeGuideDialog
  const titleText = hasLocizeTranslation(t, firstCol.titleKey, firstCol.title)
  const titleNewSize = hasLocizeTranslation(
    t,
    SIZE_TABLE_DATA.newSize.titleKey,
    SIZE_TABLE_DATA.newSize.default,
  )
  const titleOldSize = hasLocizeTranslation(
    t,
    SIZE_TABLE_DATA.oldSize.titleKey,
    SIZE_TABLE_DATA.oldSize.default,
  )
  const headerText = isNewSize ? titleNewSize : titleText
  const headerClass = isSizeGuideDialog
    ? 'header_inter-sizing'
    : 'header_new-size'
  const sizeTableClass = clsx('pml-table', {
    size_modal: isSizeGuideDialog,
  })
  const stickyClass =
    isSizeGuideDialog || isPhone
      ? { minWidth: stickyColWidth }
      : { width: stickyColWidth }

  const handleTableScroll = React.useCallback(() => {
    setScrollLeft(containerRef.current.scrollLeft)
  }, [containerRef])

  return (
    <div className={sizeTableClass}>
      <style jsx>{styles}</style>
      <div
        className="pml-table__container with-artificial-border"
        onScroll={handleTableScroll}
        ref={containerRef}
      >
        <table cellPadding={0} cellSpacing={0}>
          <thead>
            <tr className={Typo.subtitle2}>
              <th
                className={clsx(headerClass, stickyColumnClasses)}
                style={stickyClass}
              >
                {headerText}
              </th>
              {cols.map(({ extend, key, title, titleKey }) => (
                <TableHeader
                  customHeaderWidth={extend}
                  key={key}
                  title={title}
                  titleKey={titleKey}
                  className={headerClass}
                />
              ))}
            </tr>
            {isShowOldSizeRow && (
              <tr className={Typo.subtitle2}>
                <th className={stickyColumnClasses} style={stickyClass}>
                  <div className="header_old-size">{titleOldSize}</div>
                </th>
                {cols.map(({ title, titleKey }) => (
                  <TableHeader
                    title={title}
                    titleKey={titleKey}
                    childClassName="header_old-size"
                    isNewSizeFlag
                  />
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[firstCol.key]} className={Typo.body2}>
                <td
                  className={clsx(Typo.subtitle2, stickyColumnClasses)}
                  style={stickyClass}
                >
                  {row[firstCol.key]}
                </td>
                {cols.map(({ key }) => (
                  <td
                    className={clsx({
                      'content-cell': !isSizeGuideDialog,
                      'left-align': key === ' ',
                    })}
                    key={`${key}-${row[key]}`}
                  >
                    {row[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

SizeTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
      titleKey: PropTypes.string,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  t: PropTypes.func.isRequired,
  isSizeGuideDialog: PropTypes.bool,
  isNewSize: PropTypes.bool,
  isPhone: PropTypes.bool,
}

SizeTable.defaultProps = {
  rows: undefined,
  isSizeGuideDialog: false,
  isNewSize: false,
  isPhone: false,
}

export default withI18next()(SizeTable)
